<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faCow,
  faStore,
  faTruckLoading,
  faFileInvoiceDollar,
  faChartPie,
  faUsers,
  faLeaf,
  faShieldAlt,
  faMicrochip,
  faGlobe,
  faRocket,
  faHandshake
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faCow,
  faStore,
  faTruckLoading,
  faFileInvoiceDollar,
  faChartPie,
  faUsers,
  faLeaf,
  faShieldAlt,
  faMicrochip,
  faGlobe,
  faRocket,
  faHandshake
)

const animatedElements = ref<NodeListOf<Element>>()

onMounted(() => {
  animatedElements.value = document.querySelectorAll('.fade-in')
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in')
      }
    })
  }, { threshold: 0.1 })
  animatedElements.value.forEach(el => observer.observe(el))
})

const features = ref([
  {
    icon: 'cow',
    title: 'Livestock Management',
    description: 'Precision tracking for breeding, health monitoring, and production cycles. Our system ensures optimal animal welfare and yield.'
  },
  {
    icon: 'store',
    title: 'Retail & POS Operations',
    description: 'Seamless sales management for Butcher, Grocery, and LocalHarvest shops with real-time stock alerts and debt tracking.'
  },
  {
    icon: 'truck-loading',
    title: 'Operational Logistics',
    description: 'Integrated fleet and equipment management, fuel consumption analytics, and smart maintenance scheduling.'
  },
  {
    icon: 'file-invoice-dollar',
    title: 'Financial Intelligence',
    description: 'Automated expense tracking, salary management, and comprehensive profit/loss reporting with actionable insights.'
  },
  {
    icon: 'users',
    title: 'Workforce Coordination',
    description: 'Advanced worker management featuring performance ratings, emergency logs, and role-based access control.'
  },
  {
    icon: 'microchip',
    title: 'IoT & Data-Driven',
    description: 'Leveraging modern sensors and AI to provide real-time alerts and predictive analytics for proactive farm management.'
  }
])

const stats = ref([
  { value: '100%', label: 'Operational Transparency' },
  { value: '24/7', label: 'Real-time Monitoring' },
  { value: '15+', label: 'Integrated Modules' },
  { value: 'Zero', label: 'Data Redundancy' }
])

const team = ref([
  {
    name: 'Greygory Evarist Sulle',
    role: 'Lead Software Architect',
    bio: 'Architecting high-performance systems for modern agricultural infrastructure.',
    expertise: ['System Architecture', 'Security', 'FinTech']
  },
  {
    name: 'Martin Stephano Sanga',
    role: 'Technology Operations',
    bio: 'Specializing in IoT integration and real-time data analytical frameworks.',
    expertise: ['IoT Systems', 'Data Analytics', 'Backend Optimization']
  },
  {
    name: 'Salvation Herry Kamwaya',
    role: 'Full-Stack Developer',
    bio: 'Bridging the gap between complex farm logic and intuitive user experiences.',
    expertise: ['UI/UX Design', 'Vue.js', 'Operations Logic']
  }
])

const timeline = ref([
  {
    year: '2025 Q1',
    title: 'Precision Infrastructure',
    description: 'Launched the core livestock and retail modules, establishing a single source of truth for farm data.'
  },
  {
    year: '2025 Q3',
    title: 'Operational Integration',
    description: 'Expanded into logistics tracking, including equipment maintenance and fuel precision monitoring.'
  },
  {
    year: '2026 Q1',
    title: 'Financial Intelligence',
    description: 'Deployed advanced reporting engines to provide deep financial insights and automated auditing.'
  }
])
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="relative py-24 bg-gray-900 overflow-hidden">
      <!-- Abstract Background Design -->
      <div class="absolute inset-0 opacity-20">
        <div class="absolute -top-24 -left-24 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div class="absolute -bottom-24 -right-24 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
      </div>
      
      <div class="relative container mx-auto px-6 lg:px-12 text-center">
        <div class="max-w-4xl mx-auto fade-in">
          <h1 class="text-5xl md:text-7xl font-black text-white mb-8 tracking-tight">
            The Digital Heart of <span class="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400">Rochex Farm</span>
          </h1>
          <p class="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed font-medium">
            We are redefining agricultural management by blending traditional expertise with cutting-edge software engineering to build a sustainable global food future.
          </p>
          <div class="flex flex-col sm:flex-row justify-center gap-6">
            <button class="bg-green-600 hover:bg-green-700 text-white font-black py-4 px-10 rounded-2xl transition-all shadow-xl hover:shadow-green-500/20 active:scale-95">
              Explore Our Modules
            </button>
            <button class="bg-white/10 hover:bg-white/20 text-white font-black py-4 px-10 rounded-2xl transition-all backdrop-blur-md active:scale-95">
              Watch Demo
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Value Section -->
    <section class="py-24">
      <div class="container mx-auto px-6 lg:px-12">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div class="fade-in">
            <span class="text-green-600 font-black uppercase tracking-widest text-sm mb-4 block">Our Philosophy</span>
            <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-8 leading-tight">Beyond Management, We Enable Growth.</h2>
            <div class="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                At Rochex Farm, we believe that data is as vital as the soil. Our platform isn't just a record-keeping tool; it's a strategic engine designed to eliminate waste and maximize Every single asset.
              </p>
              <p>
                From the health of an individual animal to the profit margins of our retail shops, we provide 360-degree visibility into our operations, allowing us to make decisions based on facts, not assumptions.
              </p>
            </div>
            <div class="mt-12 flex gap-8">
              <div class="border-l-4 border-green-500 pl-4">
                <p class="text-2xl font-black text-gray-900 mb-1">Livestock</p>
                <p class="text-sm text-gray-500">Elite Genetic Tracking</p>
              </div>
              <div class="border-l-4 border-green-500 pl-4">
                <p class="text-2xl font-black text-gray-900 mb-1">Retail</p>
                <p class="text-sm text-gray-500">High-Velocity Sales</p>
              </div>
            </div>
          </div>
          <div class="relative fade-in">
            <div class="bg-gradient-to-br from-green-500 to-emerald-700 rounded-3xl p-1 shadow-2xl">
              <div class="bg-white rounded-[1.4rem] p-8">
                <div class="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
                  <h4 class="font-bold text-gray-900">System Capability Index</h4>
                  <span class="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-bold">Active</span>
                </div>
                <div class="space-y-6">
                  <div v-for="i in 4" :key="i" class="animate-pulse">
                    <div class="flex justify-between mb-2">
                       <div class="h-4 w-24 bg-gray-100 rounded"></div>
                       <div class="h-4 w-8 bg-gray-100 rounded"></div>
                    </div>
                    <div class="h-2 w-full bg-gray-50 rounded-full">
                       <div class="h-full bg-green-500 rounded-full" :style="{ width: (70 + (i * 5)) + '%' }"></div>
                    </div>
                  </div>
                </div>
                <div class="mt-12 grid grid-cols-2 gap-4">
                  <div class="p-4 bg-gray-50 rounded-2xl">
                    <p class="text-xs font-bold text-gray-400 uppercase mb-1">Efficiency</p>
                    <p class="text-xl font-black text-gray-900">+42%</p>
                  </div>
                  <div class="p-4 bg-gray-50 rounded-2xl">
                    <p class="text-xs font-bold text-gray-400 uppercase mb-1">Accuracy</p>
                    <p class="text-xl font-black text-gray-900">99.9%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tech Grid -->
    <section class="py-24 bg-gray-50">
      <div class="container mx-auto px-6 lg:px-12">
        <div class="text-center mb-16 fade-in">
          <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6">Built for Operational Excellence</h2>
          <p class="text-lg text-gray-600 max-w-3xl mx-auto font-medium">Our system is composed of specialized modules that talk to each other to provide a unified ecosystem.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(feature, index) in features" :key="index" class="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 group border border-gray-100 fade-in">
            <div class="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors duration-300">
              <FontAwesomeIcon :icon="feature.icon" class="text-green-600 text-2xl group-hover:text-white transition-colors duration-300" />
            </div>
            <h3 class="text-xl font-black text-gray-900 mb-3">{{ feature.title }}</h3>
            <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats -->
    <section class="py-24 bg-green-900 text-white">
      <div class="container mx-auto px-6 lg:px-12">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-12">
          <div v-for="(stat, index) in stats" :key="index" class="text-center fade-in">
            <div class="text-5xl md:text-6xl font-black text-white mb-3 tracking-tighter">{{ stat.value }}</div>
            <div class="text-green-400 font-bold uppercase tracking-widest text-xs">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Roadmap Section (Timeline) -->
    <section class="py-24 bg-white">
      <div class="container mx-auto px-6 lg:px-12">
        <h2 class="text-4xl font-black text-center mb-20 text-gray-900 fade-in">Our Strategic Roadmap</h2>
        <div class="max-w-5xl mx-auto space-y-12">
          <div v-for="(item, index) in timeline" :key="index" class="flex flex-col md:flex-row gap-8 items-center fade-in">
            <div class="md:w-1/4 text-center md:text-right">
              <span class="text-2xl font-black text-green-600">{{ item.year }}</span>
            </div>
            <div class="flex-1 bg-gray-50 rounded-3xl p-8 border-l-8 border-green-600 shadow-sm">
              <h3 class="text-xl font-black text-gray-900 mb-2">{{ item.title }}</h3>
              <p class="text-gray-600 leading-relaxed font-medium">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CEO Section -->
    <section class="py-24 bg-green-900 overflow-hidden relative">
      <div class="absolute inset-0 opacity-10">
        <svg class="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
        </svg>
      </div>
      <div class="container mx-auto px-6 lg:px-12 relative z-10">
        <div class="flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2 fade-in">
            <div class="relative inline-block">
              <div class="absolute inset-0 bg-green-500 rounded-3xl transform rotate-6 scale-105 opacity-20 transition-transform hover:rotate-2"></div>
              <img 
                src="@/assets/CEO.png" 
                alt="CEO Rochex Farm" 
                class="relative z-10 w-full max-w-md rounded-3xl shadow-2xl grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>
          <div class="lg:w-1/2 text-white fade-in">
            <span class="text-green-400 font-black uppercase tracking-widest text-sm mb-4 block">Leadership Choice</span>
            <h2 class="text-4xl md:text-5xl font-black mb-8 leading-tight">Visionary Leadership, Rooted in Accessibility.</h2>
            <div class="space-y-6 text-xl text-green-50/80 leading-relaxed font-light">
              <p>
                As the visionary behind Rochex Farm, our CEO embodies the philosophy that true leadership is about understanding and growth. He is not just the owner; he is a reachable mentor committed to the digital evolution of agriculture.
              </p>
              <p>
                Recognized for his open-door policy, he actively engages with anyone willing to learn or offer advice. His management style blends industrial scale with personal touch, ensuring that every operational module reflects our commitment to excellence and human connection.
              </p>
            </div>
            <div class="mt-12 flex items-center gap-6">
              <div class="h-1 w-12 bg-green-500"></div>
              <p class="text-2xl font-bold tracking-tight">The Visionary Founder</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Section -->
    <section class="py-24 bg-gray-50">
      <div class="container mx-auto px-6 lg:px-12 text-center">
        <h2 class="text-4xl font-black mb-20 text-gray-900 fade-in">Engineered by Visionaries</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
          <div v-for="(member, index) in team" :key="index" class="fade-in group">
            <div class="w-24 h-24 bg-green-600 rounded-3xl mx-auto mb-6 flex items-center justify-center text-white text-3xl font-black shadow-lg shadow-green-600/20 active:scale-95 transition-all">
              {{ member.name.charAt(0) }}
            </div>
            <h3 class="text-xl font-black text-gray-900 mb-1">{{ member.name }}</h3>
            <p class="text-green-600 font-bold mb-4 uppercase text-xs tracking-widest">{{ member.role }}</p>
            <p class="text-gray-600 mb-6 text-sm font-medium leading-relaxed px-4">{{ member.bio }}</p>
            <div class="flex flex-wrap justify-center gap-2">
              <span v-for="tag in member.expertise" :key="tag" class="bg-white border border-gray-200 text-gray-700 text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-tighter">
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mission -->
    <section class="py-24 bg-green-50 border-y border-green-100 italic">
      <div class="container mx-auto px-6 lg:px-12 text-center">
        <div class="max-w-4xl mx-auto fade-in">
          <p class="text-3xl md:text-4xl font-black text-green-900 mb-12 leading-tight">
            "To build a legacy of sustainable agriculture, where Every decision is empowered by clarity and Every action driven by excellence."
          </p>
          <div class="w-16 h-1 bg-green-600 mx-auto"></div>
        </div>
      </div>
    </section>

    <!-- Footer CTA -->
    <section class="py-24 bg-gray-900 text-center">
      <div class="container mx-auto px-6 lg:px-12 fade-in">
        <h2 class="text-4xl md:text-5xl font-black text-white mb-8">Ready to transform your farm?</h2>
        <p class="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">Contact us today for a full system demonstration or to join our digital movement.</p>
        <div class="flex flex-col sm:flex-row justify-center gap-6">
          <button class="bg-green-600 hover:bg-green-700 text-white font-black py-4 px-12 rounded-2xl transition-all">
             Contact Sales
          </button>
          <button class="border border-gray-700 text-white font-black py-4 px-12 rounded-2xl hover:bg-white/5 transition-all">
             Request Whitepaper
          </button>
        </div>
        <p class="mt-12 text-gray-500 font-bold">Support Hotline: +255 756 588 876 / +255 622 088 754</p>
      </div>
    </section>
  </div>
</template>

<style scoped>
.fade-in {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

.animate-fade-in {
  opacity: 1;
  transform: translateY(0);
}

@keyframes blob {
  0% { transform: scale(1); }
  33% { transform: scale(1.1); }
  66% { transform: scale(0.9); }
  100% { transform: scale(1); }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}
</style>
